import { X } from "@/components/logos/X";
import { useState, PropsWithChildren } from "react";
import {
  deleteTweet,
  GeneratedContent,
  TweetRating,
  updateTweet,
} from "@/api/twitter";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { TwitterAvatar } from "@/components/twitter/TwitterAvatar";
import { Persona } from "@/api/personas";
import { LoadingButton } from "@/components/LoadingButton";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { CalendarIcon, PencilIcon, UserIcon } from "lucide-react";
import { Rating } from "./ui/rating";
import { type CreateJob, createScheduledJob } from "@/api/scheduledJobs";
import { AddJobDialog } from "@/components/automate/AddJobDialog";
import { useAuth } from "@/hooks/use-auth.tsx";
import { TweetImageSelector } from "@/components/content-creation/TweetImageSelector";
import { PostTweetButton } from "@/components/content-creation/PostTweetButton";
import { TweetImage } from "./content-creation/TweetImage";
import { PostToInstagramButton } from "./content-creation/PostToInstagramButton";
import { Role } from "./AuthorizationProvider";
import { TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { Tooltip } from "./ui/tooltip";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";

const TweetCard = ({
  tweet,
  personaData,
  onDelete,
  onRate,
}: {
  tweet: GeneratedContent;
  personaData: Persona;
  onDelete: () => void;
  onRate: (ratings: TweetRating[]) => void;
}) => {
  const { toast } = useToast();
  const { hasAtLeastRole } = useAuth();
  const dateToDisplay = tweet.date ? new Date(tweet.date) : new Date();
  const localDate = dateToDisplay.toLocaleString(undefined, {
    timeZoneName: "short",
  });
  const ratings = tweet.ratings ?? [];
  const totalRating = ratings.reduce((sum, rating) => sum + rating.rating, 0);
  const averageRating = ratings.length > 0 ? totalRating / ratings.length : 0;
  const isAdmin = hasAtLeastRole(Role.TenantAdmin);

  // State for Schedule Job Dialog
  const [showScheduleDialog, setShowScheduleDialog] = useState<
    "twitter" | "instagram" | null
  >(null);

  // Handle creating a scheduled job via the dialog
  const handleCreateJob = async (job: CreateJob) => {
    try {
      // Make sure the tweet_id is in the metadata
      if (!job.metadata) {
        job.metadata = {};
      }
      job.metadata.tweet_id = tweet.doc_id;

      await createScheduledJob(job);
      toast({ description: "Tweet scheduled successfully" });
      return Promise.resolve();
    } catch (error) {
      console.error("Error scheduling tweet:", error);
      toast({ description: "Failed to schedule tweet. Please try again." });
      return Promise.reject(error);
    }
  };

  // Handle tweet update (after edit)
  const handleTweetUpdate = () => {
    onRate(tweet.ratings || []); // Using onRate to refresh the tweet
    toast({ description: "Tweet updated successfully" });
  };

  return (
    <div className="bg-background border rounded-2xl px-4 sm:px-6 py-4">
      <div className="flex justify-between items-center">
        <div className="w-full flex flex-row justify-between items-center flex-wrap gap-2">
          <TwitterAvatar persona={personaData} />
          <span className="text-xs order-3 sm:order-2 w-full sm:w-auto text-right">
            {localDate}
          </span>
        </div>
      </div>

      <p className="mt-2 text-sm">
        {tweet.text} {tweet.emoji}
        {tweet.hashtags &&
        tweet.hashtags.filter((tag) => tag.trim() !== "").length > 0
          ? tweet.hashtags
              .filter((tag) => tag.trim() !== "")
              .map((tag) => ` #${tag}`)
              .join("")
          : ""}
      </p>

      <div className="mt-2">
        {tweet.sources?.length > 0 &&
          tweet.sources.map((source, index) => (
            <Link
              key={index}
              className="text-sm text-primary break-all"
              to={source}
              target="_blank"
              rel="noopener noreferrer"
            >
              {source}
            </Link>
          ))}
      </div>

      <div className="flex flex-row justify-start gap-x-4 overflow-x-auto w-full py-4">
        {tweet.images.map((id) => (
          <TweetImage key={id} id={id} className="h-12" />
        ))}
      </div>

      <div className="flex flex-col gap-3 justify-between items-center mt-3 pt-2 border-t border-border">
        <div className="w-full flex flex-row gap-2 items-center justify-between flex-wrap">
          <div className="flex flex-row gap-2 items-center mb-2 sm:mb-0">
            <UserIcon className="w-4 h-4" />
            <Rating
              value={averageRating / 2}
              max={5}
              precision={0.5}
              readOnly
            />
          </div>

          <RateAlertDialog tweet={tweet} onRate={onRate}>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full px-4 sm:px-6"
              >
                Rate
              </Button>
            </AlertDialogTrigger>
          </RateAlertDialog>
        </div>

        <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex flex-row gap-2 items-center justify-start sm:justify-start flex-wrap">
            <EditTweetDialog tweet={tweet} onEdit={handleTweetUpdate}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1 rounded-full flex-1 sm:flex-none"
                >
                  <PencilIcon className="w-4 h-4" />
                  <span className="sm:inline">Edit</span>
                </Button>
              </AlertDialogTrigger>
            </EditTweetDialog>
            <DeleteAlertDialog tweet={tweet} onDelete={onDelete}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:bg-destructive hover:text-destructive-foreground rounded-full flex-1 sm:flex-none"
                >
                  Delete
                </Button>
              </AlertDialogTrigger>
            </DeleteAlertDialog>
          </div>
          <div className="flex flex-row gap-2 items-center justify-between sm:justify-end flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 rounded-full flex-1 sm:flex-none"
              onClick={() => setShowScheduleDialog("twitter")}
            >
              <CalendarIcon className="w-4 h-4" />
              <span className="sm:inline">Schedule to Twitter</span>
            </Button>
            {isAdmin && (
              /* Trick to show tooltips on disabled buttons: https://github.com/shadcn-ui/ui/issues/1022#issuecomment-2691383699 */
              <Tooltip
                delayDuration={0}
                open={tweet.images.length > 0 ? false : undefined}
              >
                <TooltipTrigger asChild>
                  <span tabIndex={0}>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={tweet.images.length === 0}
                      className="flex items-center gap-1 rounded-full flex-1 sm:flex-none"
                      onClick={() => setShowScheduleDialog("instagram")}
                    >
                      <CalendarIcon className="w-4 h-4" />
                      <span className="sm:inline">Schedule to Instagram</span>
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Images are required for Instagram posts. Please add an
                    image.{" "}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
            <PostTweetButton persona={personaData} tweet={tweet} />
            {isAdmin && (
              <PostToInstagramButton persona={personaData} content={tweet} />
            )}
          </div>
        </div>
      </div>

      {/* AddJobDialog for scheduling */}
      {showScheduleDialog && (
        <AddJobDialog
          onClose={() => setShowScheduleDialog(null)}
          onSave={handleCreateJob}
          selectedPersonaId={personaData.persona_id}
          initialProduct={
            showScheduleDialog === "twitter"
              ? "twitter-posting-post-existing-tweet"
              : "instagram-posting-post-existing-content"
          }
          initialTweetId={tweet.doc_id}
          showProductSelector={false}
        />
      )}
    </div>
  );
};

function RateAlertDialog({
  children,
  tweet,
  onRate,
}: PropsWithChildren<{
  tweet: GeneratedContent;
  onRate: (ratings: TweetRating[]) => void;
}>) {
  const { toast } = useToast();
  const { username } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ratings = tweet.ratings ?? [];
  const rating = ratings.find((rating) => rating.username === username);
  const [userRating, setUserRating] = useState(rating?.rating ?? 0);
  const [feedback, setFeedback] = useState(rating?.feedback ?? "");

  const handleSubmitRating = async () => {
    try {
      const ratings = tweet.ratings || [];
      const updatedRatings = [...ratings];
      const userIndex = updatedRatings.findIndex(
        (r) => r.username === username,
      );

      const newRating: TweetRating = {
        username: username || "",
        rating: userRating,
        feedback,
      };

      if (userIndex > -1) {
        updatedRatings[userIndex] = newRating;
      } else {
        updatedRatings.push(newRating);
      }

      await updateTweet(tweet.doc_id, { ratings: updatedRatings });
      onRate(updatedRatings);

      setOpen(false);
    } catch (error) {
      console.error("[handleSubmitRating] Failed to update tweet:", error);
      toast({ description: "Failed to rate tweet. Please try again later." });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      {children}
      <AlertDialogContent
        /* Do not close alert on escape key press when loading */
        onEscapeKeyDown={loading ? (e) => e.preventDefault() : undefined}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Rate this Tweet</AlertDialogTitle>
        </AlertDialogHeader>
        <div className="flex flex-col items-center">
          <p>
            {tweet.text} {tweet.emoji}
          </p>
          <p className="mt-4 text-muted-foreground">
            Please provide your rating and detailed feedback for this tweet.
          </p>
          <Rating
            value={userRating / 2}
            max={5}
            precision={0.5}
            onChange={(newValue) => setUserRating(2 * (newValue ?? 0))}
          />
          <div className="mt-2 w-full flex flex-col gap-2">
            <Label htmlFor="feedback">Your Feedback</Label>
            <Textarea
              id="feedback"
              rows={4}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>

          <AlertDialogAction asChild>
            <LoadingButton
              loading={loading}
              onClick={(e) => {
                e.preventDefault();

                setLoading(true);

                handleSubmitRating().finally(() => setLoading(false));
              }}
            >
              Submit
            </LoadingButton>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditTweetDialog({
  children,
  tweet,
  onEdit,
}: PropsWithChildren<{
  tweet: GeneratedContent;
  onEdit?: () => void;
}>) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tweetText, setTweetText] = useState(tweet.text || "");
  const [emoji, setEmoji] = useState(tweet.emoji || "");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [hashtags, setHashtags] = useState(
    tweet.hashtags ? tweet.hashtags.join(", ") : "",
  );
  const [sources, setSources] = useState(
    tweet.sources ? tweet.sources.join("\n") : "",
  );
  const [selectedImages, setSelectedImages] = useState<string[]>(
    tweet.images || [],
  );

  const handleSubmitEdit = async () => {
    try {
      // Validate tweet text
      if (!tweetText.trim()) {
        toast({ description: "Tweet text cannot be empty." });
        return;
      }

      // Parse hashtags from comma-separated string to array
      const hashtagsArray = hashtags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag !== "");

      // Parse sources from newline-separated string to array
      const sourcesArray = sources
        .split("\n")
        .map((source) => source.trim())
        .filter((source) => source !== "");

      const updatedTweet = {
        text: tweetText,
        emoji,
        hashtags: hashtagsArray,
        sources: sourcesArray,
        images: selectedImages,
      };

      await updateTweet(tweet.doc_id, updatedTweet);

      // Call onEdit callback if provided
      if (onEdit) {
        onEdit();
      }

      setOpen(false);
    } catch (error) {
      console.error("[handleSubmitEdit] Failed to update tweet:", error);
      toast({ description: "Failed to update tweet. Please try again later." });
    }
  };

  // Format hashtags for preview
  const previewHashtags = hashtags
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag !== "")
    .map((tag) => `#${tag}`)
    .join(" ");

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      {children}
      <AlertDialogContent
        onEscapeKeyDown={loading ? (e) => e.preventDefault() : undefined}
        className="max-w-4xl grid-rows-[auto_1fr_auto]"
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Edit Tweet</AlertDialogTitle>
        </AlertDialogHeader>

        <div className="flex flex-col space-y-6 overflow-y-auto">
          {/* Tweet Text - full width */}
          <div>
            <Label htmlFor="tweet-text">Tweet Text</Label>
            <Textarea
              id="tweet-text"
              rows={5}
              value={tweetText}
              onChange={(e) => setTweetText(e.target.value)}
              className="mt-1 w-full"
            />
          </div>
          <div>
            <Label htmlFor="tweet-images">Images</Label>
            <TweetImageSelector
              id="tweet-images"
              className="mt-4"
              personaId={tweet.persona_id}
              value={selectedImages}
              onChange={setSelectedImages}
            />
          </div>

          {/* Emoji and Hashtags in a row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="relative">
              <Label htmlFor="tweet-emoji">Emoji</Label>
              <div className="flex items-center gap-2 mt-1">
                <Textarea
                  id="tweet-emoji"
                  rows={1}
                  value={emoji}
                  onChange={(e) => setEmoji(e.target.value)}
                  className="w-full"
                  placeholder="Optional emoji"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEmojiPicker((prev) => !prev)}
                >
                  😊
                </Button>
              </div>

              {showEmojiPicker && (
                <div className="absolute z-50 mt-2">
                  <Picker
                    data={data}
                    onEmojiSelect={(emojiObj: any) => {
                      setEmoji((prev) => prev + emojiObj.native);
                      setShowEmojiPicker(false);
                    }}
                    theme="light"
                    previewPosition="none"
                  />
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="tweet-hashtags">Hashtags</Label>
              <Textarea
                id="tweet-hashtags"
                rows={1}
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                className="mt-1"
                placeholder="Comma-separated hashtags"
              />
            </div>
          </div>

          {/* Sources - full width */}
          <div>
            <Label htmlFor="tweet-sources">Sources (one per line)</Label>
            <Textarea
              id="tweet-sources"
              rows={3}
              value={sources}
              onChange={(e) => setSources(e.target.value)}
              className="mt-1 w-full"
              placeholder="Enter sources (one URL per line)"
            />
          </div>

          {/* Preview - full width */}
          <div>
            <Label className="flex items-center justify-between">
              <span>Tweet Preview</span>
              <div>
                <X width={16} height={16} fill="white" />
              </div>
            </Label>
            <div className="border rounded-2xl p-4 bg-background mt-1">
              <p className="text-sm">
                {tweetText} {emoji}
                {previewHashtags && (
                  <span className="text-primary"> {previewHashtags}</span>
                )}
              </p>

              <div className="mt-4">
                {sources.split("\n").map(
                  (source, index) =>
                    source.trim() !== "" && (
                      <div
                        key={index}
                        className="text-sm text-primary underline mt-1"
                      >
                        {source}
                      </div>
                    ),
                )}
              </div>
              <div className="flex flex-row justify-start gap-x-4 overflow-x-auto w-full mt-4">
                {selectedImages.map((id) => (
                  <TweetImage key={id} id={id} className="h-12" />
                ))}
              </div>
            </div>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction asChild>
            <LoadingButton
              loading={loading}
              onClick={(e) => {
                e.preventDefault();
                setLoading(true);
                handleSubmitEdit().finally(() => setLoading(false));
              }}
            >
              Save Changes
            </LoadingButton>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DeleteAlertDialog({
  children,
  tweet,
  onDelete,
}: PropsWithChildren<{
  tweet: GeneratedContent;
  onDelete: () => void;
}>) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [feedback, setFeedback] = useState("");

  const handleConfirmDelete = async () => {
    try {
      await deleteTweet(tweet.doc_id, {
        deleted: true,
        feedback,
        rating: userRating,
      });

      setOpen(false);

      // Call the parent's `onDelete` callback to remove the tweet from the list
      onDelete();
      toast({ description: "Tweet deleted successfully" });
    } catch (error) {
      console.error(
        "[handleConfirmDelete] Failed to mark tweet as deleted:",
        error,
      );
      toast({ description: "Failed to delete tweet. Please try again later." });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      {children}
      <AlertDialogContent
        /* Do not close alert on escape key press when loading */
        onEscapeKeyDown={loading ? (e) => e.preventDefault() : undefined}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this Tweet</AlertDialogTitle>
        </AlertDialogHeader>
        <div className="flex flex-col items-center">
          <p>{tweet.text}</p>
          <Rating
            value={userRating / 2}
            max={5}
            precision={0.5}
            onChange={(newValue) => setUserRating((newValue ?? 0) * 2)}
          />
          <div className="mt-2 w-full flex flex-col gap-2">
            <Label htmlFor="feedback">Reason for Deletion</Label>
            <Textarea
              id="feedback"
              rows={4}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction asChild variant="destructive">
            <LoadingButton
              loading={loading}
              onClick={(e) => {
                // Prevents the dialog from closing
                e.preventDefault();

                if (userRating === 0) {
                  alert("Please provide a rating before deleting.");
                  return;
                }

                setLoading(true);
                handleConfirmDelete().finally(() => setLoading(false));
              }}
            >
              Confirm Delete
            </LoadingButton>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default TweetCard;
